import React, { useMemo, useState } from 'react';

const TEAMS = ['A', 'B', 'C', 'D'];

function downloadFile(filename, content, mime = 'text/csv') {
  const blob = new Blob([content], { type: `${mime};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


function escapeHtml(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function DropInRosterExport({ registrations = [] }) {
  const [sessionId, setSessionId] = useState('');

  // Group active (non-removed/refunded) registrations by session
  const sessions = useMemo(() => {
    const map = new Map();
    registrations
      .filter(r => r.payment_status !== 'admin_removed' && r.payment_status !== 'refunded')
      .forEach(r => {
        if (!r.session_id) return;
        if (!map.has(r.session_id)) {
          map.set(r.session_id, {
            id: r.session_id,
            label: r.session_label || r.session_id,
            time: r.session_time || '',
            location: r.session_location || '',
            level: r.level || '',
            count: 0,
          });
        }
        map.get(r.session_id).count += 1;
      });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [registrations]);

  const selected = sessions.find(s => s.id === sessionId) || null;

  const byTeam = useMemo(() => {
    if (!sessionId) return null;
    const buckets = { A: [], B: [], C: [], D: [], '-': [] };
    registrations
      .filter(r =>
        r.session_id === sessionId &&
        r.payment_status !== 'admin_removed' &&
        r.payment_status !== 'refunded'
      )
      .forEach(r => {
        const key = r.team_letter && buckets[r.team_letter] !== undefined ? r.team_letter : '-';
        buckets[key].push({
          name: r.user_name || '(no name)',
          email: r.user_email || '',
          position: r.position || '',
          payment: r.payment_status || (r.paid ? 'paid' : 'pending'),
          confirmation: r.confirmation_number || '',
        });
      });
    Object.values(buckets).forEach(arr =>
      arr.sort((a, b) => a.name.localeCompare(b.name))
    );
    return buckets;
  }, [registrations, sessionId]);

  const exportExcel = () => {
    if (!selected || !byTeam) return;
    const sessionTag = `${selected.label}${selected.time ? ' · ' + selected.time : ''}${selected.location ? ' · ' + selected.location : ''}${selected.level ? ' · ' + selected.level.toUpperCase() : ''}`;

    const ovrRed = '#9D1515';
    const headerStyle = `background:${ovrRed};color:#fff;font-family:'Bebas Neue','Oswald','Arial Black',sans-serif;font-size:14pt;letter-spacing:2px;padding:10px 14px;border:1px solid #7a1010;text-align:left`;
    const subHeaderStyle = `background:#1a1a1a;color:#fff;font-family:'Arial',sans-serif;font-size:9pt;font-weight:bold;letter-spacing:1.5px;text-transform:uppercase;padding:8px 12px;border:1px solid #333`;
    const cellStyle = `font-family:'Arial',sans-serif;font-size:10pt;padding:7px 12px;border:1px solid #ddd;color:#222`;
    const positionCellStyle = `${cellStyle};font-family:'Courier New',monospace;font-weight:bold;color:${ovrRed};text-align:center`;
    const paidStyle = `${cellStyle};color:#1d7a3d;font-weight:bold;text-transform:uppercase;font-size:9pt;text-align:center`;
    const pendingStyle = `${cellStyle};color:#b8770a;font-weight:bold;text-transform:uppercase;font-size:9pt;text-align:center`;
    const emptyStyle = `${cellStyle};color:#999;font-style:italic;text-align:center`;
    const teamCountStyle = `background:${ovrRed};color:#fff;font-family:'Courier New',monospace;font-size:11pt;font-weight:bold;padding:10px 14px;border:1px solid #7a1010;text-align:right`;

    const buildTeamTable = (letter, rows) => {
      const head = `<tr>
        <th colspan="4" style="${headerStyle}">TEAM ${letter}</th>
        <th style="${teamCountStyle}">${rows.length} / 6</th>
      </tr>
      <tr>
        <th style="${subHeaderStyle}">#</th>
        <th style="${subHeaderStyle}">Player Name</th>
        <th style="${subHeaderStyle}">Position</th>
        <th style="${subHeaderStyle}">Payment</th>
        <th style="${subHeaderStyle}">Confirmation</th>
      </tr>`;
      const body = rows.length === 0
        ? `<tr><td colspan="5" style="${emptyStyle}">- No players assigned -</td></tr>`
        : rows.map((r, i) => `<tr>
            <td style="${cellStyle};text-align:center;color:#666;font-family:'Courier New',monospace">${i + 1}</td>
            <td style="${cellStyle};font-weight:600">${escapeHtml(r.name)}</td>
            <td style="${positionCellStyle}">${escapeHtml(r.position || '-')}</td>
            <td style="${r.payment === 'paid' ? paidStyle : pendingStyle}">${escapeHtml(r.payment)}</td>
            <td style="${cellStyle};font-family:'Courier New',monospace;font-size:9pt;color:#666">${escapeHtml(r.confirmation)}</td>
          </tr>`).join('');
      return `<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;margin-bottom:24px;width:760px">
        ${head}${body}
      </table>`;
    };

    const titleBar = `<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;margin-bottom:6px;width:760px">
      <tr><td style="background:#000;color:#fff;font-family:'Bebas Neue','Oswald','Arial Black',sans-serif;font-size:28pt;letter-spacing:4px;padding:18px 20px;border:3px solid ${ovrRed}">
        OVR DROP-IN ROSTER
      </td></tr>
    </table>
    <table cellspacing="0" cellpadding="0" style="border-collapse:collapse;margin-bottom:24px;width:760px">
      <tr><td style="background:#f3f3f3;color:#444;font-family:'Arial',sans-serif;font-size:10pt;padding:10px 16px;border:1px solid #ccc;border-top:none">
        ${escapeHtml(sessionTag)}
      </td></tr>
    </table>`;

    const teams = TEAMS.concat(byTeam['-'].length ? ['-'] : [])
      .map(letter => buildTeamTable(letter, byTeam[letter] || []))
      .join('');

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"><title>OVR Drop-In Roster</title>
        <!--[if gte mso 9]><xml>
          <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
            <x:Name>Roster</x:Name>
            <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
          </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
        </xml><![endif]-->
      </head>
      <body>${titleBar}${teams}</body></html>`;

    const fname = `OVR-Drop-In-Roster-${selected.id}.xls`;
    downloadFile(fname, html, 'application/vnd.ms-excel');
  };

  const printRoster = () => {
    if (!selected || !byTeam) return;
    const sessionTag = `${selected.label}${selected.time ? ' · ' + selected.time : ''}${selected.location ? ' · ' + selected.location : ''}${selected.level ? ' · ' + selected.level.toUpperCase() : ''}`;
    const teamsHtml = TEAMS.concat(byTeam['-'].length ? ['-'] : []).map(letter => {
      const rows = byTeam[letter] || [];
      const rowsHtml = rows.length === 0
        ? '<tr><td colspan="2" style="padding:8px 10px;color:#999;font-style:italic;text-align:center">Empty</td></tr>'
        : rows.map(r => `<tr>
            <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(r.name)}</td>
            <td style="padding:6px 10px;border-bottom:1px solid #eee;font-weight:600">${escapeHtml(r.position || '-')}</td>
          </tr>`).join('');
      return `<div style="break-inside:avoid;border:1px solid #ccc;page-break-inside:avoid">
        <div style="padding:8px 12px;background:#000;color:#fff;display:flex;justify-content:space-between;align-items:center">
          <span style="font-family:'Bebas Neue','Oswald',sans-serif;font-size:22px;letter-spacing:0.08em">TEAM ${letter}</span>
          <span style="font-family:'JetBrains Mono',monospace;font-size:11px">${rows.length}/6</span>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:13px">
          <thead><tr style="background:#f3f3f3">
            <th style="text-align:left;padding:6px 10px;font-size:10px;letter-spacing:0.16em;color:#666;text-transform:uppercase">Name</th>
            <th style="text-align:left;padding:6px 10px;font-size:10px;letter-spacing:0.16em;color:#666;text-transform:uppercase">Position</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>`;
    }).join('');

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Drop-In Roster - ${escapeHtml(selected.label)}</title>
      <style>
        body{font-family:'Inter',system-ui,sans-serif;color:#000;background:#fff;padding:32px;margin:0}
        h1{font-family:'Bebas Neue','Oswald',sans-serif;letter-spacing:0.06em;margin:0 0 6px}
        .sub{color:#444;font-size:12px;margin-bottom:24px}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
        @media print { .grid{grid-template-columns:1fr 1fr} body{padding:16px} }
      </style></head>
      <body>
        <h1>DROP-IN ROSTER</h1>
        <div class="sub">${escapeHtml(sessionTag)}</div>
        <div class="grid">${teamsHtml}</div>
        <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { alert('Pop-up blocked. Allow pop-ups for this site to print.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const cellStyle = { padding: '8px 12px', fontSize: 13, borderBottom: '1px solid #1a1a1a' };
  const thStyle = { ...cellStyle, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', color: '#666', textTransform: 'uppercase', textAlign: 'left' };

  return (
    <div className="dropin-export" style={{ background: '#0d0d0d', border: '1px solid #1e1e1e', borderTop: '2px solid #9D1515', padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 20, letterSpacing: '0.08em', color: '#fff' }}>
          ROSTER EXPORT
        </div>
        <select
          value={sessionId}
          onChange={e => setSessionId(e.target.value)}
          style={{ background: '#111', border: '1px solid #2a2a2a', color: '#e8e8e8', padding: '8px 12px', fontSize: 12, fontFamily: 'Inter, sans-serif', minWidth: 260 }}
        >
          <option value="">Select a session…</option>
          {sessions.map(s => (
            <option key={s.id} value={s.id}>
              {s.label}{s.time ? ` · ${s.time}` : ''} ({s.count})
            </option>
          ))}
        </select>
        <button disabled={!sessionId} onClick={exportExcel}
          style={{ background: '#9D1515', border: 'none', color: '#fff', fontFamily: "'Bebas Neue',sans-serif", fontSize: 12, letterSpacing: '0.12em', padding: '8px 14px', cursor: sessionId ? 'pointer' : 'not-allowed', opacity: sessionId ? 1 : 0.4 }}>
          EXPORT EXCEL
        </button>
        <button disabled={!sessionId} onClick={printRoster}
          style={{ background: 'transparent', border: '1px solid #3a3a3a', color: '#ccc', fontFamily: "'Bebas Neue',sans-serif", fontSize: 12, letterSpacing: '0.12em', padding: '8px 14px', cursor: sessionId ? 'pointer' : 'not-allowed', opacity: sessionId ? 1 : 0.4 }}>
          PRINT
        </button>
      </div>

      {selected && byTeam && (
        <>
          <div style={{ marginBottom: 12, fontSize: 12, color: '#888' }}>
            <b style={{ color: '#fff' }}>{selected.label}</b>
            {selected.time && <> · {selected.time}</>}
            {selected.location && <> · {selected.location}</>}
            {selected.level && <> · {selected.level.toUpperCase()}</>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {TEAMS.map(letter => {
              const rows = byTeam[letter] || [];
              return (
                <div key={letter} style={{ background: '#111', border: '1px solid #1e1e1e' }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid #2a2a2a', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, color: '#fff', letterSpacing: '0.06em' }}>TEAM {letter}</div>
                    <div style={{ fontSize: 11, color: '#666', fontFamily: "'JetBrains Mono', monospace" }}>{rows.length}/6</div>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Position</th>
                    </tr></thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={2} style={{ ...cellStyle, color: '#444', textAlign: 'center', fontStyle: 'italic' }}>Empty</td></tr>
                      ) : rows.map((r, i) => (
                        <tr key={i}>
                          <td style={{ ...cellStyle, color: '#e8e8e8' }}>{r.name}</td>
                          <td style={{ ...cellStyle, color: '#9D1515', fontWeight: 600 }}>{r.position || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
          {byTeam['-'].length > 0 && (
            <div style={{ marginTop: 16, fontSize: 12, color: '#c8860a', background: 'rgba(200,134,10,0.08)', border: '1px solid rgba(200,134,10,0.2)', padding: '8px 12px' }}>
              ⚠ {byTeam['-'].length} player{byTeam['-'].length === 1 ? '' : 's'} have no team assigned (legacy registration).
            </div>
          )}
        </>
      )}
    </div>
  );
}
